import api from './api'

/**
 * Event Service
 * Handles all event-related API calls
 */
class EventService {
  constructor() {
    // Service initialized
  }
  /**
   * Get all events for a club
   * GET v1/{club}/events
   */
  async getEvents(clubUuid: string, dateFrom?: string, dateTo?: string, perPage: number = 50, page: number = 1, categories?: string[]): Promise<any> {
    const params: any = {
      per_page: perPage,
      page: page
    }

    if (dateFrom) {
      params.date_from = dateFrom
    }

    if (dateTo) {
      params.date_to = dateTo
    }

    if (categories && categories.length > 0) {
      params.categories = categories.join(',')
    }

    const response = await api.get(`v1/${clubUuid}/events`, { params })
    return response.data
  }

  /**
   * Get single event
   * GET v1/{club}/events/{event}
   */
  async getEvent(clubUuid: string, eventUuid: string, date?: string): Promise<any> {
    const params: any = {}
    if (date) {
      params.date = date
    }
    const response = await api.get(`v1/${clubUuid}/events/${eventUuid}`, { params })
    return response.data
  }

  /**
   * Delete event
   * DELETE v1/{club}/events/{event}
   */
  async deleteEvent(clubUuid: string, eventUuid: string, date?: string): Promise<any> {
    const params: any = {}
    if (date) {
      params.date = date
    }
    const response = await api.delete(`v1/${clubUuid}/events/${eventUuid}`, { params })
    return response.data
  }

  /**
   * Create new event
   * POST v1/{club}/events
   */
  async createEvent(clubUuid: string, data: {
    title: string
    type: 'SINGLE' | 'CYCLE'
    category_type: string
    place?: string
    start_date: string
    start_time?: string
    end_date?: string
    end_time?: string
    cycle_period?: string
    cycle_end_date?: string
    coaches?: string[]
    players?: string[]
    categories?: string[]
  }): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/events`, data)
    return response.data
  }

  /**
   * Update event
   * PUT v1/{club}/events/{event}
   */
  async updateEvent(clubUuid: string, eventUuid: string, data: {
    title: string
    category_type: string
    place?: string
    start_date: string
    start_time?: string
    end_date?: string
    end_time?: string
    date?: string
    coaches?: string[]
    players?: string[]
    categories?: string[]
  }): Promise<any> {
    const response = await api.put(`v1/${clubUuid}/events/${eventUuid}`, data)
    return response.data
  }

  /**
   * Get event participants
   * GET v1/{club}/events/{event}/participants
   */
  async getParticipants(clubUuid: string, eventUuid: string, date?: string): Promise<any> {
    const params: any = {}
    if (date) params.date = date
    const response = await api.get(`v1/${clubUuid}/events/${eventUuid}/participants`, { params })
    return response.data
  }

  /**
   * Get event attendance
   * GET v1/{club}/events/{event}/attendance
   */
  async getAttendance(clubUuid: string, eventUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/events/${eventUuid}/attendance`)
    return response.data
  }

  /**
   * Save event attendance
   * POST v1/{club}/events/{event}/attendance
   */
  async saveAttendance(clubUuid: string, eventUuid: string, data: {
    players: Array<{
      player_uuid: string
      is_present: boolean
      absence_reason: string | null
    }>
    coaches: Array<{
      coach_uuid: string
      is_present: boolean
      absence_reason: string | null
    }>
  }, date?: string): Promise<any> {
    const params: any = {}
    if (date) {
      params.date = date
    }
    const response = await api.post(`v1/${clubUuid}/events/${eventUuid}/attendance`, data, { params })
    return response.data
  }

  /**
   * Update event attendance
   * PUT v1/{club}/events/{event}/attendance
   */
  async updateAttendance(clubUuid: string, eventUuid: string, data: {
    players: Array<{
      player_uuid: string
      is_present: boolean
      absence_reason: string | null
    }>
    coaches: Array<{
      coach_uuid: string
      is_present: boolean
      absence_reason: string | null
    }>
  }): Promise<any> {
    const response = await api.put(`v1/${clubUuid}/events/${eventUuid}/attendance`, data)
    return response.data
  }

  /**
   * Get cycle events for a club
   * GET v1/{club}/events/cycles
   */
  async getCycleEvents(clubUuid: string, perPage: number = 25, page: number = 1): Promise<any> {
    const params: any = {
      per_page: perPage,
      page: page
    }

    const response = await api.get(`v1/${clubUuid}/events/cycles`, { params })
    return response.data
  }

  /**
   * Delete cycle event
   * DELETE v1/{club}/events/{event}/cycle
   */
  async deleteCycle(clubUuid: string, eventUuid: string): Promise<any> {
    const response = await api.delete(`v1/${clubUuid}/events/${eventUuid}/cycle`)
    return response.data
  }

  /**
   * Get cycle event details
   * GET v1/{club}/events/{event}/cycle
   */
  async getCycleEvent(clubUuid: string, eventUuid: string): Promise<any> {
    const response = await api.get(`v1/${clubUuid}/events/${eventUuid}/cycle`)
    return response.data
  }

  /**
   * Update cycle event
   * PUT v1/{club}/events/{event}/cycle
   */
  async updateCycleEvent(clubUuid: string, eventUuid: string, data: {
    title: string
    category_type: string
    place?: string
    start_time?: string
    end_time?: string
    cycle_period?: string
    cycle_end_date?: string
    coaches?: string[]
    players?: string[]
    categories?: string[]
  }): Promise<any> {
    const response = await api.put(`v1/${clubUuid}/events/${eventUuid}/cycle`, data)
    return response.data
  }

  /**
   * Request attendance report generation (queued job)
   * POST v1/{club}/events/attendance-report
   */
  async requestAttendanceReport(clubUuid: string, data: {
    date_from: string
    date_to: string
    player_uuids: string[]
    coach_uuids: string[]
  }): Promise<any> {
    const response = await api.post(`v1/${clubUuid}/events/attendance-report`, data)
    return response.data
  }
}

export default new EventService()
